import { useState } from 'react';
import { CoachAnalysis, GRADE_META } from './coachAnalysis';
import { shortWon } from './tabData';

/**
 * 강사 인사이트 카드 — 오늘의 브리핑 목록에 한 장 서는 강사 건.
 *
 * ⚠️ 이 카드는 **브리핑 항목이 아닙니다.**
 *    강사 현황 탭이 쓰는 데이터(getCoaches → analyzeCoach)에서 뽑은 한 명이라
 *    `itemId` 가 없고, 그래서 **가리기·완료 기록을 걸 수 없습니다.**
 *    로컬 state 로 숨기지 않습니다 — 예전 「넘어가기」가 정확히 그렇게 동작해서
 *    새로고침하면 되돌아오는데 화면은 "다시 알려드릴게요" 라고 말했습니다.
 *    가릴 수 있게 하려면 서버에 자리가 있어야 합니다
 *    (BACKEND_HANDOFF_OPS_BRIEFING_ACTIONS.md 참고).
 *
 * 생김새는 BriefingCard 의 **데이터 영역과 같은 틀**입니다 — 등급 레일,
 * 좌측 순번 칸, 판단 문장, 근거 스트립, 우측 지표 패널. 회원 카드 넉 장 사이에
 * 다른 모양이 한 장 끼면 그 줄만 눈에서 튀고, 대표님은 그것을 "오류"로 읽습니다.
 *
 * 카드를 누르면 **강사 데이터 드로어**(CoachDataDrawer)가 열리고, 그 안의
 * '월간 리포트(PDF)' 버튼으로 강사 현황 탭과 **같은 완성본 리포트**로 갑니다.
 * 카드 아래 '월간 리포트 보기' 버튼은 그 인쇄물로 곧장 갑니다.
 *
 * 어느 문으로 들어가든 숫자는 하나여야 합니다 — 드로어도 리포트도
 * coachAnalysis 가 만든 같은 분석 결과를 받습니다. 여기서 요약본을 따로 그리면
 * 두 화면의 판정이 갈린 것처럼 보입니다.
 */

const BORDER = '#E3EAF3';

type Props = {
    analysis: CoachAnalysis;
    /** 상세(리포트)가 열려 있는 카드인가 */
    selected: boolean;
    /**
     * 카드를 눌렀다 — **강사 데이터 드로어**를 연다.
     *
     * 예전에는 여기서 곧바로 PDF 가 열렸다. 대표 요청으로 데이터가 먼저 오고,
     * 인쇄물은 아래 '월간 리포트 보기' 버튼(onOpenReport)으로 간다.
     */
    onOpen: (coachId: string) => void;
    /**
     * 아래 '월간 리포트 보기' 버튼 — 곧장 PDF 로 간다.
     *
     * 안 넘기면 카드 클릭과 같게 동작한다(구버전 호출부 호환). 다만 그러면
     * 버튼 이름이 거짓말을 하므로 되도록 넘긴다.
     */
    onOpenReport?: (coachId: string) => void;
};

const CoachInsightCard = ({ analysis, selected, onOpen, onOpenReport }: Props) => {
    const { coach, topIssue, grade } = analysis;
    const meta = GRADE_META[grade];
    const [hover, setHover] = useState(false);

    /* 집중 관리만 붉은 레일 — 점검 필요까지 붉게 칠하면 위험 회원 카드와 급이 같아진다 */
    const rail = grade === 'CRITICAL' ? '#FF3E4A' : '#F0A020';
    const tint = grade === 'CRITICAL' ? '#FFF7F7' : '#FFFCF3';

    /*
     * 진단 문장은 판정 함수가 고른 1순위 약점으로 만든다.
     * 강사 현황 탭과 같은 판정(analyzeCoach)이라 두 화면의 말이 갈리지 않는다.
     *
     * ⚠️ 방향은 지표마다 다르다. 노쇼율은 **높을수록** 나쁘다 —
     *    전부 "낮습니다" 로 적으면 노쇼가 많은 강사를 "노쇼율이 낮다" 고 말한다.
     */
    const diagnosis = topIssue
        ? `${topIssue.metric.label} 지표가 센터 평균보다 ${
              topIssue.metric.higherIsBetter ? '낮습니다' : '높습니다'
          }`
        : meta.desc;

    /*
     * 근거 스트립 — 회원 카드의 facts 와 같은 자리, 같은 크기.
     * 값은 전부 판정 함수가 계산한 것이다. 여기서 새로 만들지 않는다.
     */
    const facts: { label: string; value: string; alert?: boolean }[] = [
        ...(topIssue
            ? [
                  {
                      label: `${topIssue.metric.label} (이 강사)`,
                      value: `${topIssue.value}${topIssue.metric.unit}`,
                      alert: true,
                  },
                  {
                      label: '센터 평균',
                      value: `${topIssue.avg}${topIssue.metric.unit}`,
                  },
              ]
            : []),
        { label: '담당 회원', value: `${coach.members}명` },
        { label: '수업', value: `${coach.lessons}회` },
    ];

    const open = () => onOpen(coach.coachId);
    /* 버튼 이름이 '월간 리포트'면 월간 리포트가 나와야 한다 */
    const openReport = () => (onOpenReport ?? onOpen)(coach.coachId);

    return (
        <div
            role="button"
            tabIndex={0}
            aria-label={`${coach.name} 강사 상세 보기`}
            aria-expanded={selected}
            onClick={open}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    open();
                }
            }}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            className="flex w-full overflow-hidden rounded-[14px] border border-solid bg-white"
            style={{
                borderColor: selected ? rail : BORDER,
                boxShadow: selected
                    ? `0 0 0 3px ${meta.bg}`
                    : hover
                      ? '0 6px 18px rgba(16,32,64,0.10)'
                      : '0 1px 2px rgba(16,32,64,0.04)',
                cursor: 'pointer',
                transition: 'box-shadow .15s, border-color .15s',
            }}
        >
            {/* 좌측 등급 레일 */}
            <div style={{ width: 5, flex: 'none', background: rail }} />

            {/*
              순번 칸.
              회원 카드에는 여기 완료 체크박스가 있지만 강사 건에는 없다 —
              서버 브리핑 문서에 없는 건이라 완료로 기록할 자리가 없다.
              빈 네모를 그려 놓고 눌러도 아무 일이 없게 두지 않는다.
            */}
            <div
                className="flex flex-none flex-col items-center"
                style={{ width: 66, paddingTop: 15, background: tint }}
            >
                <span
                    className="flex items-center justify-center"
                    style={{
                        width: 34,
                        height: 34,
                        borderRadius: 9,
                        border: `2px dashed ${rail}`,
                        color: '#B4BDC8',
                        fontSize: 15,
                        cursor: 'help',
                    }}
                    title="강사 현황에서 가져온 건이라 완료로 기록할 수 없습니다"
                >
                    —
                </span>
                {/*
                  🔴 **번호를 달지 않는다** (2026-08-31 QA9).
                     번호 1~5 는 「오늘 챙길 일」 회원 카드의 것이다. 이 줄이
                     6 을 달면 화면이 「5건」이라 말해 놓고 6개를 세게 된다.
                     이 카드의 성격은 등급 배지와 색이 말한다.
                */}
            </div>

            {/*
              본문.
              회원 카드(BriefingCard 의 목록 모드)와 **같은 여백**을 쓴다.
              한 줄만 두툼하면 그 카드가 오류처럼 튄다.
            */}
            <div className="flex min-w-0 flex-1 flex-col" style={{ padding: '14px 22px 14px 8px' }}>
                <div className="flex items-center gap-[10px]">
                    <span
                        className="rounded-[6px]"
                        style={{
                            background: meta.bg,
                            color: meta.color,
                            fontSize: 12.5,
                            fontWeight: 800,
                            padding: '4px 10px',
                            letterSpacing: '0.02em',
                        }}
                    >
                        {meta.label}
                    </span>
                    <span style={{ fontSize: 17, fontWeight: 700, color: '#16202E' }}>
                        {coach.name} 강사
                    </span>
                    <span style={{ fontSize: 15, color: '#93A0AE' }}>담당 {coach.members}명</span>
                    <span
                        className="ml-auto"
                        style={{ fontSize: 12.5, color: '#93A0AE', whiteSpace: 'nowrap' }}
                        title="브리핑 항목이 아니라 강사 현황 판정에서 가져온 건입니다"
                    >
                        강사 현황
                    </span>
                </div>

                {/* 판단 문장 — 회원 카드와 같은 급으로 세운다 */}
                <div style={{ marginTop: 9 }}>
                    <span
                        style={{
                            fontSize: 23,
                            fontWeight: 700,
                            color: '#0F1723',
                            lineHeight: '32px',
                            letterSpacing: '-0.02em',
                        }}
                    >
                        {diagnosis}
                    </span>
                </div>

                {/* 근거 수치 */}
                <div
                    className="flex w-full items-stretch rounded-[12px]"
                    style={{
                        marginTop: 11,
                        border: `1px solid ${BORDER}`,
                        background: '#FCFDFE',
                        overflow: 'hidden',
                    }}
                >
                    {facts.map((f, i) => (
                        <div
                            key={`${f.label}-${i}`}
                            className="flex flex-1 flex-col items-center justify-center"
                            style={{
                                padding: '11px 12px',
                                borderLeft: i === 0 ? 'none' : `1px solid ${BORDER}`,
                                background: f.alert ? tint : 'transparent',
                                minWidth: 0,
                            }}
                        >
                            <span
                                style={{
                                    fontSize: 12.5,
                                    color: f.alert ? meta.color : '#93A0AE',
                                    fontWeight: f.alert ? 700 : 400,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: '100%',
                                }}
                            >
                                {f.label}
                            </span>
                            <span
                                style={{
                                    fontSize: f.value.length > 6 ? 19 : 26,
                                    fontWeight: 800,
                                    color: f.alert ? meta.color : '#16202E',
                                    letterSpacing: '-0.03em',
                                    marginTop: 4,
                                    lineHeight: '32px',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {f.value}
                            </span>
                        </div>
                    ))}
                </div>

                {/*
                  회원 카드는 여기에 '오늘 할 일'과 지시 버튼이 있다.
                  강사 건에는 없다 — 지시는 (회원, 담당 강사) 조합으로만 만들어지고,
                  이 건이 요구하는 것은 면담 자리를 잡는 일이라 성격이 다르다.
                  무엇을 이야기할지는 카드를 눌러 여는 데이터 드로어에서 정한다.

                  여기 "카드를 누르면 월간 리포트가 열립니다" 라는 안내 줄이 있었다.
                  ① 카드를 누르면 이제 **데이터 드로어**가 열린다 — 문장이 틀렸다.
                  ② 회원 카드에는 없는 줄이라 이 카드만 한 줄 더 길어졌다.
                */}
            </div>

            {/*
              우측 지표 패널 — 회원 카드의 목록 모드와 **같은 압축 문법**.
              제목 한 줄 · 평균 → 이 강사 한 줄 · 대비 배지 · 텍스트 링크.
              예전의 34px 숫자와 46px 버튼은 이 패널만 세로 220px 로 만들어서,
              왼쪽이 스트립에서 끝나는 카드에 빈칸을 만들었다.
            */}
            <div
                className="flex flex-none flex-col justify-center"
                style={{
                    width: 236,
                    padding: '14px 18px',
                    borderLeft: '1px solid #EDF1F7',
                    background: '#FBFCFE',
                }}
            >
                {topIssue ? (
                    <>
                        <span
                            style={{
                                fontSize: 12.5,
                                color: '#5B6B7F',
                                letterSpacing: '-0.01em',
                                fontWeight: 700,
                                lineHeight: '17px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {topIssue.metric.label}
                        </span>

                        <div className="flex items-baseline" style={{ marginTop: 5, gap: 6 }}>
                            <span
                                style={{
                                    fontSize: 15,
                                    fontWeight: 700,
                                    color: '#A7B0BC',
                                    letterSpacing: '-0.02em',
                                    whiteSpace: 'nowrap',
                                }}
                                title="센터 평균"
                            >
                                {topIssue.avg}
                                {topIssue.metric.unit}
                            </span>

                            <span style={{ fontSize: 13, color: '#C4CCD8' }}>→</span>

                            <span
                                style={{
                                    fontSize: 22,
                                    fontWeight: 800,
                                    lineHeight: '26px',
                                    letterSpacing: '-0.03em',
                                    color: meta.color,
                                    whiteSpace: 'nowrap',
                                }}
                                title="이 강사"
                            >
                                {topIssue.value}
                                {topIssue.metric.unit}
                            </span>
                        </div>

                        {/*
                          평균 대비 몇 %인가 — 부호를 그대로 적는다.
                          "▼ 감소" 처럼 좋고 나쁨을 말로 붙이지 않는다. 노쇼율은
                          올라간 것이 나쁜 것이라, 같은 말이 지표마다 뜻이 뒤집힌다.

                          회원 카드와 달리 값 옆에 붙이지 않는다 — 이 문장은
                          '센터 평균 대비' 라는 기준까지 말해야 해서 한 줄이 필요하다.
                        */}
                        <span
                            className="inline-flex items-center self-start rounded-[6px]"
                            style={{
                                marginTop: 6,
                                background: meta.bg,
                                color: meta.color,
                                fontSize: 12.5,
                                fontWeight: 800,
                                padding: '3px 8px',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            센터 평균 대비 {topIssue.diffPct > 0 ? '+' : ''}
                            {topIssue.diffPct}%
                        </span>
                    </>
                ) : (
                    <>
                        <span
                            style={{
                                fontSize: 12.5,
                                color: '#5B6B7F',
                                fontWeight: 700,
                                lineHeight: '17px',
                            }}
                        >
                            담당 미사용 선수금
                        </span>
                        <span
                            style={{
                                fontSize: 22,
                                fontWeight: 800,
                                lineHeight: '26px',
                                letterSpacing: '-0.03em',
                                color: '#16202E',
                                marginTop: 5,
                            }}
                        >
                            {shortWon(coach.portfolioValue)} 원
                        </span>
                    </>
                )}

                {/*
                  강사 카드에는 8주 추이가 없다.
                  강사에게는 주당 수업 추이라는 개념이 없고, 0 으로 채운 막대를 그리면
                  "이 강사는 수업이 없다" 로 읽힌다.

                  '담당 미사용 선수금' 을 여기 한 번 더 적던 줄도 뺐다 —
                  카드 왼쪽 근거 스트립과 드로어에 같은 값이 있다.
                */}
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        openReport();
                    }}
                    className="flex items-center"
                    style={{
                        marginTop: 10,
                        alignSelf: 'flex-start',
                        background: 'transparent',
                        border: 'none',
                        padding: 0,
                        color: '#20406F',
                        fontSize: 12.5,
                        fontWeight: 700,
                        cursor: 'pointer',
                        gap: 4,
                    }}
                >
                    📄 월간 리포트 보기
                    <span style={{ fontSize: 13, opacity: 0.6 }}>›</span>
                </button>
            </div>
        </div>
    );
};

export default CoachInsightCard;
